/* eslint-disable import/prefer-default-export */
import React, { useState, useEffect, useRef } from 'react';
import { General } from 'icons/untitledui';

import type { TopBarMenuItem } from 'topBar/components/TopBar';
import { DropdownMenu } from 'shared/components/dropdown/DropdownMenu';
import * as Buttons from 'topBar/styles/TopBarButtons.style';
import * as Menu from 'topBar/styles/TopBarMenu.style';
import * as Text from 'theme/components/Text.style';

interface MultiItemMenuProps {
  items: TopBarMenuItem[];
  menuButtonAriaLabel: string;
}

export function MultiItemMenu({
  items,
  menuButtonAriaLabel,
}: MultiItemMenuProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSubItem, setActiveSubItem] = useState<TopBarMenuItem | null>(
    null
  );
  const [showingSuccessIndex, setShowingSuccessIndex] = useState<number | null>(
    null
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  useEffect(() => {
    if (!isMenuOpen) setActiveSubItem(null);
  }, [isMenuOpen]);

  const renderedItems = activeSubItem?.subItems ?? items;
  const headerLabel = activeSubItem?.subHeaderLabel;

  const dropdownItems = renderedItems.map((item, index) => {
    const displayLabel =
      !activeSubItem && showingSuccessIndex === index && item.successMessage
        ? item.successMessage
        : item.label;

    return {
      label: <Text.P2>{displayLabel}</Text.P2>,
      disabled: item.disabled,
      disabledReason: item.disabledReason,
      icon: item.icon,
      primary: item.primary,
      destructive: item.destructive,
      onSelect: ({ close }: { close: () => void }) => {
        if (item.disabled) return;

        if (item.subItems) {
          setActiveSubItem(item);
          return;
        }

        item.onClick();

        if (item.successMessage) {
          setShowingSuccessIndex(index);
          timeoutRef.current = setTimeout(() => {
            setShowingSuccessIndex(null);
            close();
          }, 3000);
        } else if (item.delayBeforeClose) {
          timeoutRef.current = setTimeout(close, item.delayBeforeClose);
        } else {
          close();
        }
      },
    };
  });

  return (
    <Menu.MenuWrapper>
      <DropdownMenu
        isOpen={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        usePortal
        items={dropdownItems}
        headerLabel={headerLabel}
        renderTrigger={({ isOpen, toggle }) => (
          <Buttons.MenuTriggerButton
            onClick={toggle}
            aria-haspopup="true"
            aria-expanded={isOpen}
            aria-label={menuButtonAriaLabel}
          >
            <General.DotsHorizontal size={20} />
          </Buttons.MenuTriggerButton>
        )}
      />
    </Menu.MenuWrapper>
  );
}
